/**
 * Shared Deep Rest player: one <audio> element for the whole app, so a
 * session keeps playing while the user moves between tabs, and a mini-player
 * can sit above the nav. The card's player and the mini-player both drive
 * this. Reports one play per session start (POST /rest/{id}/played) and
 * exposes `finishedId` so the card can ask for feedback after a full listen.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { markRestPlayed } from './api';

const RestPlayerContext = createContext(null);

export function RestPlayerProvider({ children }) {
  const audioRef = useRef(null);
  const reportedRef = useRef(null);
  const [session, setSession] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(null);
  const [finishedId, setFinishedId] = useState(null);

  if (!audioRef.current && typeof window !== 'undefined') {
    const a = new window.Audio();
    a.preload = 'metadata';
    audioRef.current = a;
  }

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return undefined;
    const onPlay = () => { setPlaying(true); setError(null); };
    const onPause = () => setPlaying(false);
    const onEnded = () => { setPlaying(false); setFinishedId((s) => session?.id || s); };
    const onTime = () => setTime(a.currentTime || 0);
    const onMeta = () => { if (Number.isFinite(a.duration)) setDuration(a.duration); };
    const onErr = () => setError('This session could not load. Open Today again for a fresh link.');
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    a.addEventListener('ended', onEnded);
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('loadedmetadata', onMeta);
    a.addEventListener('error', onErr);
    return () => {
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('ended', onEnded);
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('loadedmetadata', onMeta);
      a.removeEventListener('error', onErr);
    };
  }, [session?.id]);

  const skip = useCallback((delta) => {
    const a = audioRef.current;
    if (!a) return;
    const max = a.duration || duration || 0;
    a.currentTime = Math.max(0, Math.min(max, (a.currentTime || 0) + delta));
  }, [duration]);

  useEffect(() => {
    if (!('mediaSession' in navigator) || !session) return;
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: session.title || 'Deep Rest', artist: 'The Navigator', album: 'Deep Rest',
      });
      navigator.mediaSession.setActionHandler('play', () => audioRef.current?.play());
      navigator.mediaSession.setActionHandler('pause', () => audioRef.current?.pause());
      navigator.mediaSession.setActionHandler('seekbackward', () => skip(-15));
      navigator.mediaSession.setActionHandler('seekforward', () => skip(15));
    } catch { /* unsupported */ }
  }, [session, skip]);

  const load = useCallback(async (next, { autoplay = false } = {}) => {
    const a = audioRef.current;
    if (!a || !next?.play_url) return;
    if (session?.id !== next.id) {
      a.src = next.play_url;
      setSession(next);
      setTime(0);
      setDuration(next.duration_seconds || 0);
      setFinishedId(null);
      reportedRef.current = null;
    }
    if (autoplay) {
      try { await a.play(); } catch { setError('Tap play to start. Some phones need the tap.'); }
    }
  }, [session?.id]);

  const toggle = useCallback(async () => {
    const a = audioRef.current;
    if (!a || !session) return;
    try {
      if (a.paused) {
        await a.play();
        if (reportedRef.current !== session.id) {
          reportedRef.current = session.id;
          markRestPlayed(session.id).catch(() => {});
        }
      } else {
        a.pause();
      }
    } catch {
      setError('Tap play again to start. Some phones need a second tap.');
    }
  }, [session]);

  const stop = useCallback(() => {
    const a = audioRef.current;
    if (a) { a.pause(); a.removeAttribute('src'); a.load(); }
    setSession(null); setPlaying(false); setTime(0); setDuration(0);
  }, []);

  const value = useMemo(() => ({ session, playing, time, duration, error, finishedId, load, toggle, skip, stop,
    clearFinished: () => setFinishedId(null) }), [session, playing, time, duration, error, finishedId, load, toggle, skip, stop]);

  return <RestPlayerContext.Provider value={value}>{children}</RestPlayerContext.Provider>;
}

export function useRestPlayer() {
  return useContext(RestPlayerContext);
}

export function fmtTime(sec) {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}
